import { type ModelRef, type Ref, isRef, ref } from 'vue';

const useLoadState = (
  opts: { isLoadingRef?: Ref<boolean> | ModelRef<boolean>; once?: boolean } = {},
) => {
  const { isLoadingRef = null, once = false } = opts;

  let isFirstLoad: boolean = true;

  const isLoadSuccess = ref<boolean>(false);
  const isLoadError = ref<boolean>(false);
  const isLoading = isLoadingRef && isRef(isLoadingRef) ? isLoadingRef : ref<boolean>(false);

  const startLoad = (): void => {
    if (once && !isFirstLoad) {
      return;
    }

    isLoadSuccess.value = false;
    isLoadError.value = false;
    isLoading.value = true;

    isFirstLoad = false;
  };

  const endLoad = (options: { isSuccess?: boolean; isError?: boolean } = {}): void => {
    isLoadSuccess.value = options.isSuccess ?? false;
    isLoadError.value = options.isError ?? false;
    isLoading.value = false;

    isFirstLoad = false;
  };

  const endLoadSuccess = (): void => {
    endLoad({ isSuccess: true });
  };

  const endLoadError = (): void => {
    endLoad({ isError: true });
  };

  const stateHook =
    <TArgs extends unknown[], TReturn>(fn: (...args: TArgs) => TReturn | Promise<TReturn>) =>
    async (...args: TArgs): Promise<TReturn | undefined> => {
      if (once && !isFirstLoad) {
        return undefined;
      }

      startLoad();
      try {
        const result = await fn(...args);
        endLoad({ isSuccess: true });
        return result;
      } catch {
        endLoad({ isError: true });
      }
    };

  return {
    isLoadSuccess,
    isLoadError,
    isLoading,
    startLoad,
    endLoad,
    endLoadSuccess,
    endLoadError,
    stateHook,
  };
};

export { useLoadState };
